import { CampaignPhase, CampaignPostDTO, PostNotPublishableReason, PostStatus, PostTextAction, PostTextStatus, SocialCampaignDTO } from 'models/socialCampaign'
import {
  PostAddress,
  PostDraft,
  afterRemoval,
  changesOf,
  editorModeOf,
  isEmptyChange,
  nextDraftToDecide,
  postTextStatusOf
} from 'utils/socialCampaign/postEditor'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import {
  AskedPostText,
  SocialCampaignTarget,
  isPostTextRefused,
  isSocialCampaignConflict,
  postsNotPublishableOf,
  useAddSocialCampaignPost,
  useApproveAllSocialCampaignPosts,
  useApproveSocialCampaignPost,
  useAskedSocialCampaignTexts,
  useRemoveSocialCampaignPost,
  useRequestSocialCampaignPostText,
  useUndoSocialCampaignPostApproval,
  useUpdateSocialCampaignPost
} from 'dataQueries/socialCampaign.query'
import {
  SocialCampaignEditorSource,
  SocialCampaignEventProperties,
  failurePropertiesOf,
  httpStatusOf,
  joinValues,
  refusalPropertiesOf,
  trackSocialCampaignEvent,
  trackedPostOf,
  wentLive
} from 'utils/analytics/socialCampaign'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import axios from 'axios'
import { formatCampaignDay } from 'utils/socialCampaign/campaignStatus'
import { logger } from 'utils/logging'
import { useCampaignFlash } from './CampaignFlash.context'
import { usePostEditorAddress } from './usePostEditorAddress'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/** What the editor says when the backend refused or failed an action; the editor stays on the post. */
export type PostEditorError =
  /** 422: posts that would go out cannot, each with why (the post itself, or other drafts a launch would schedule) */
  | { kind: 'not_publishable', posts: { subject: string, reasons: PostNotPublishableReason[] }[] }
  /** 409: the post changed state meanwhile (it went out, entered the edit freeze, the campaign ended elsewhere) */
  | { kind: 'conflict' }
  /** 400 on an AI action: the text leaves nothing to rewrite (nothing to shorten, no room left under the limit) */
  | { kind: 'text_refused' }
  /** Anything else: the change could not be saved */
  | { kind: 'failed' }

/** What an action leads to; the editor plays the walk itself (the slide on a phone). */
export type PostEditorOutcome =
  | { kind: 'walk', to: string }
  /** The last draft decided scheduled every draft: the editor closed and the page says what went on the schedule */
  | { kind: 'launched' }
  /** The editor closed (removed, created, done) */
  | { kind: 'closed' }
  /** The approval was taken back: the editor stays on the draft */
  | { kind: 'undone' }
  /** An AI text action was asked, the form saved first: the editor stays on the post while the text is rewritten */
  | { kind: 'asked' }
  /** Undo after an AI text action: the text it replaced is saved back, the editor stays on the post */
  | { kind: 'restored' }
  /** Refused or failed: the error shows in the editor */
  | { kind: 'stay' }

interface Options {
  /** Re-reads the campaign after a conflict */
  refetchCampaign: () => void
  /** The tab, where the rows are */
  rootRef: RefObject<HTMLElement>
  titleRef: RefObject<HTMLElement>
}

const drafts = (posts: CampaignPostDTO[]) => posts.filter((post) => post.status === PostStatus.DRAFT)

type PostActionProperties = SocialCampaignEventProperties[AnalyticsEvent.SOCIAL_CAMPAIGN_POST_ACTION_SUBMITTED]
type TextActionProperties = SocialCampaignEventProperties[AnalyticsEvent.SOCIAL_CAMPAIGN_TEXT_ACTION_SUBMITTED]

/** What a save is told as: a change of the client's, or Undo saving back the text an AI action replaced. */
type SavedAs = Pick<PostActionProperties, 'action' | 'textAction'>
const EDIT: SavedAs = { action: 'edit' }

const askedKeyOf = (item: AskedPostText) => `${item.postId}@${item.askedAt}`

/** What the analytics say of an action that did not go through: how it was answered, and what a 422 named. */
const failedAction = (failure: unknown) => ({
  ...failurePropertiesOf(failure),
  ...refusalPropertiesOf(postsNotPublishableOf(failure)),
})

/**
 * The post editor's controller (ISSUE-3624): its address, the post it shows, and every action of its three modes, each
 * telling its outcome. Changes are kept in the editor's form and saved when the client leaves the post (approving it,
 * walking to another, closing it, "Save changes"), so typed text is never lost and the backend gets one change per
 * visit. The last draft decided launches the campaign (prototype v3 `launch`): the editor closes and the page says what
 * was scheduled. Every outcome is read from the backend's answer, not predicted: another tab may have moved things.
 */
export function usePostEditor(target: SocialCampaignTarget, campaign: SocialCampaignDTO | null | undefined, options: Options) {
  const { refetchCampaign, rootRef, titleRef } = options
  const { t, i18n } = useTranslation(['social_campaign'])
  const address = usePostEditorAddress()
  const { showFlash } = useCampaignFlash()
  const { spawnSuccessToast } = useSnackbar()
  const update = useUpdateSocialCampaignPost()
  const approve = useApproveSocialCampaignPost()
  const undo = useUndoSocialCampaignPostApproval()
  const remove = useRemoveSocialCampaignPost()
  const add = useAddSocialCampaignPost()
  const approveAll = useApproveAllSocialCampaignPosts()
  const rewrite = useRequestSocialCampaignPostText()
  const textActions = useAskedSocialCampaignTexts(target)
  const [error, setError] = useState<PostEditorError | null>(null)
  // whether the page is still there to say what happened (another tab of the property takes it away with the editor)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const where = address.address
  const whereNow = useRef(where)
  whereNow.current = where

  // Back took the address of a new post that holds something (the editor says so after each commit, `reportNew`): it
  // stays on screen, its address gone, until "Discard this post?" is answered. Not the hook's own closes, nor a post
  // whose creation is on its way
  const newHoldsSomething = useRef(false)
  const ownClose = useRef(false)
  const [backedOut, setBackedOut] = useState<Extract<PostAddress, { kind: 'new' }> | null>(null)
  const [seen, setSeen] = useState(where)
  if (seen !== where) {
    setSeen(where)
    setBackedOut(seen?.kind === 'new' && !where && newHoldsSomething.current && !ownClose.current && !add.isPending ? seen : null)
  }
  useEffect(() => {
    ownClose.current = false
  }, [where])

  const postId = where?.kind === 'post' ? where.postId : null
  const post = postId ? campaign?.posts.find((item) => item.id === postId) ?? null : null
  const newPost = where ? (where.kind === 'new' ? where : null) : backedOut
  const creating = !!newPost && !!campaign && campaign.phase !== CampaignPhase.ENDED
  const isOpen = !!campaign && (post !== null || creating)
  useEffect(() => {
    if (backedOut && !isOpen) setBackedOut(null)
  }, [backedOut, isOpen])

  // Each opening is told once, with what opened it; a walk to another post is not an opening (ISSUE-3637)
  const openedFrom = useRef<SocialCampaignEditorSource>('address')
  const toldOpen = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      toldOpen.current = false
      return
    }
    if (toldOpen.current || !campaign) return
    toldOpen.current = true
    const source = openedFrom.current
    openedFrom.current = 'address'
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_EDITOR_OPENED, target, campaign, { source, mode: editorModeOf(post), ...trackedPostOf(post) })
  }, [campaign, isOpen, post, target])

  // the post a removal is taking away: the answer drops it from the cache before the walk replaces the address
  const removing = useRef<string | null>(null)
  if (removing.current && removing.current !== postId) removing.current = null

  // An address naming nothing the page can open (a post removed or re-planned elsewhere, an ended campaign's new post,
  // no campaign at all): dropped in place, without walking history (prototype `syncEditor`)
  const unopenable = !!where && campaign !== undefined && !isOpen && !(postId && removing.current === postId)
  const { dismiss } = address
  useEffect(() => {
    if (!unopenable) return
    ownClose.current = true
    // the click's source belongs to this opening, which never happened: the next one without a source is the address's
    openedFrom.current = 'address'
    dismiss()
  }, [unopenable, dismiss])

  // An error belongs to the post it was said on
  useEffect(() => setError(null), [postId, where?.kind])

  // Where the focus lands once the editor closed: after the Modal gave it back to whatever opened it
  const focusAfterClose = useRef<{ postId: string } | 'title' | null>(null)
  useEffect(() => {
    if (isOpen || !focusAfterClose.current) return
    const target = focusAfterClose.current
    focusAfterClose.current = null
    window.setTimeout(() => {
      const row = target !== 'title' ? rootRef.current?.querySelector<HTMLElement>(`[data-post="${target.postId}"]`) : null
      if (row instanceof HTMLButtonElement) row.focus()
      else titleRef.current?.focus()
    }, 0)
  }, [isOpen, rootRef, titleRef])

  // an action answered once the editor was gone (Back, another tab) has nothing left to close: closing would walk history
  const closeTo = useCallback((focus: { postId: string } | 'title') => {
    if (!mounted.current || !whereNow.current) return
    focusAfterClose.current = focus
    ownClose.current = true
    address.close()
  }, [address])

  const subjectsOf = useCallback((posts: CampaignPostDTO[]) => new Map(posts.map((item) => [item.id, item.subject])), [])

  const trackAction = useCallback((properties: PostActionProperties) => {
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_POST_ACTION_SUBMITTED, target, campaign, properties)
  }, [campaign, target])

  const tellTextAnswer = useCallback((answered: CampaignPostDTO, textAction: PostTextAction, since: number, from: SocialCampaignDTO) => {
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_TEXT_ACTION_FINISHED, target, from, {
      textAction,
      outcome: postTextStatusOf(answered) === PostTextStatus.READY ? 'written' : 'failed',
      durationMs: Math.max(0, Date.now() - since),
      ...trackedPostOf(answered),
    })
  }, [target])
  // An AI text action is answered once it leaves `textActions` after a read showed it PENDING; one let go before is no
  // answer. The list moves before the campaign query hands the read over: the answer waits for its post to show it.
  // Those listed before the tab mounted were asked by a visit the client left: no request of this page.
  const askedBeforeMount = useRef(new Set(textActions.map(askedKeyOf)))
  const followedTexts = useRef(textActions)
  const answeredTexts = useRef<AskedPostText[]>([])
  useEffect(() => {
    const left = followedTexts.current.filter((item) =>
      item.pendingSeen && !askedBeforeMount.current.has(askedKeyOf(item)) && !textActions.some((next) => next.postId === item.postId))
    followedTexts.current = textActions
    answeredTexts.current.push(...left)
    if (!campaign) return
    answeredTexts.current = answeredTexts.current.filter((item) => {
      const answered = campaign.posts.find((post) => post.id === item.postId)
      if (answered && postTextStatusOf(answered) === PostTextStatus.PENDING) return true
      // a post gone meanwhile (removed, re-planned) has no answer to tell
      if (answered) tellTextAnswer(answered, item.action, item.askedAt, campaign)
      return false
    })
  }, [campaign, tellTextAnswer, textActions])

  /** Says why an action did not go through; a conflict also re-reads the campaign, so the editor shows it as it is. */
  const refused = useCallback((failure: unknown, subjects: Map<string, string>): PostEditorOutcome => {
    const notPublishable = postsNotPublishableOf(failure)
    if (notPublishable) {
      setError({
        kind: 'not_publishable',
        posts: notPublishable.posts.map((item) => ({ subject: subjects.get(item.postId) ?? t('flash.unknown_post'), reasons: item.reasons })),
      })
    } else if (isSocialCampaignConflict(failure)) {
      setError({ kind: 'conflict' })
      refetchCampaign()
    } else {
      setError({ kind: 'failed' })
      // a 400 means the page and the backend disagree on the post: show it as the backend has it
      if (axios.isAxiosError(failure) && failure.response?.status === 400) refetchCampaign()
      // a throw of the page's own (storing the answer): no request log reports it
      if (!axios.isAxiosError(failure)) {
        logger.error('Social campaign post editor action failed without an HTTP answer', failure, {
          context: { component: 'usePostEditor', metadata: { campaignId: campaign?.id } },
        })
      }
    }
    return { kind: 'stay' }
  }, [campaign?.id, refetchCampaign, t])

  /**
   * After an approval or a removal: when the drafts that were there are all decided now, the last one scheduled them
   * (a draft campaign is live) and the page says so; else the review walks on to the post given, else it is over.
   */
  const afterDecision = useCallback((before: CampaignPostDTO[], answer: SocialCampaignDTO, currentId: string, walkTo: string | null): PostEditorOutcome => {
    const kept = new Set(answer.posts.map((item) => item.id))
    // the drafts still in the campaign (a removed one is not): all scheduled at once by the last decision
    const decided = drafts(before).filter((item) => kept.has(item.id)).map((item) => item.id)
    if (decided.length > 0 && drafts(answer.posts).length === 0) {
      const wereDrafts = new Set(decided)
      showFlash({
        kind: 'launched',
        scheduled: answer.posts.filter((item) => wereDrafts.has(item.id) && item.status === PostStatus.SCHEDULED).length,
        waiting: answer.posts.filter((item) => item.status === PostStatus.WAITING_FOR_CONTENT).length,
      })
      closeTo('title')
      return { kind: 'launched' }
    }
    if (walkTo && kept.has(walkTo)) return { kind: 'walk', to: walkTo }
    closeTo(kept.has(currentId) ? { postId: currentId } : 'title')
    return { kind: 'closed' }
  }, [closeTo, showFlash])

  /** Saves what changed in the form, if anything; false when the backend refused it (the error shows). */
  const save = useCallback(async (current: CampaignPostDTO, draft: PostDraft, savedAs: SavedAs = EDIT) => {
    if (!campaign) return false
    const changes = changesOf(draft, current, campaign.timezone, campaign.networks)
    if (isEmptyChange(changes)) return true
    setError(null)
    const tracked = { ...savedAs, via: 'editor' as const, changedFields: joinValues(Object.keys(changes)), ...trackedPostOf(current) }
    try {
      await update.mutateAsync({ ...target, postId: current.id, body: changes })
    } catch (failure) {
      trackAction({ ...tracked, ...failedAction(failure) })
      refused(failure, subjectsOf(campaign.posts))
      return false
    }
    trackAction({ ...tracked, outcome: 'ok' })
    return true
  }, [campaign, refused, subjectsOf, target, trackAction, update])

  /** "Approve & next", "Approve & launch campaign": saves the form, approves, then walks on or launches. */
  const approvePost = useCallback(async (current: CampaignPostDTO, draft: PostDraft): Promise<PostEditorOutcome> => {
    if (!campaign || !(await save(current, draft))) return { kind: 'stay' }
    const before = campaign.posts
    const tracked = { action: 'approve' as const, ...trackedPostOf(current) }
    let answer: SocialCampaignDTO
    try {
      answer = await approve.mutateAsync({ ...target, postId: current.id })
    } catch (failure) {
      trackAction({ ...tracked, ...failedAction(failure) })
      return refused(failure, subjectsOf(before))
    }
    trackAction({ ...tracked, outcome: 'ok', launched: wentLive(campaign, answer) })
    return afterDecision(before, answer, current.id, nextDraftToDecide(answer.posts, current.id)?.id ?? null)
  }, [afterDecision, approve, campaign, refused, save, subjectsOf, target, trackAction])

  /** "Launch campaign" on the last draft, already approved: every draft scheduled at once. */
  const launch = useCallback(async (current: CampaignPostDTO, draft: PostDraft): Promise<PostEditorOutcome> => {
    if (!campaign || !(await save(current, draft))) return { kind: 'stay' }
    const before = campaign.posts
    const tracked = { action: 'launch' as const, ...trackedPostOf(current) }
    let answer: SocialCampaignDTO
    try {
      answer = await approveAll.mutateAsync(target)
    } catch (failure) {
      trackAction({ ...tracked, ...failedAction(failure) })
      return refused(failure, subjectsOf(before))
    }
    trackAction({ ...tracked, outcome: 'ok', launched: wentLive(campaign, answer) })
    return afterDecision(before, answer, current.id, null)
  }, [afterDecision, approveAll, campaign, refused, save, subjectsOf, target, trackAction])

  /**
   * "Undo approval": the draft is to decide again. What was typed (`draft`, when it can be saved) is saved first, so
   * leaving while the undo is on its way keeps it; otherwise the form keeps it.
   */
  const undoApproval = useCallback(async (current: CampaignPostDTO, draft: PostDraft | null): Promise<PostEditorOutcome> => {
    if (!campaign || (draft && !(await save(current, draft)))) return { kind: 'stay' }
    setError(null)
    const tracked = { action: 'undo_approval' as const, ...trackedPostOf(current) }
    try {
      await undo.mutateAsync({ ...target, postId: current.id })
    } catch (failure) {
      trackAction({ ...tracked, ...failedAction(failure) })
      return refused(failure, subjectsOf(campaign.posts))
    }
    trackAction({ ...tracked, outcome: 'ok' })
    spawnSuccessToast(t('editor.toast.unapproved'))
    return { kind: 'undone' }
  }, [campaign, refused, save, spawnSuccessToast, subjectsOf, t, target, trackAction, undo])

  /**
   * Removes the post, once confirmed. While reviewing the walk goes on (the post after, else the one before); the last
   * draft to decide, removed, schedules the approved others. Otherwise the editor closes.
   */
  const removePost = useCallback(async (current: CampaignPostDTO, reviewing: boolean): Promise<PostEditorOutcome> => {
    if (!campaign) return { kind: 'stay' }
    const before = campaign.posts
    const following = reviewing ? afterRemoval(before, current.id)?.id ?? null : null
    setError(null)
    removing.current = current.id
    const tracked = { action: 'remove' as const, ...trackedPostOf(current) }
    let answer: SocialCampaignDTO
    try {
      answer = await remove.mutateAsync({ ...target, postId: current.id })
    } catch (failure) {
      removing.current = null
      trackAction({ ...tracked, ...failedAction(failure) })
      return refused(failure, subjectsOf(before))
    }
    trackAction({ ...tracked, outcome: 'ok', launched: wentLive(campaign, answer) })
    const outcome = afterDecision(before, answer, current.id, following)
    if (outcome.kind !== 'launched') spawnSuccessToast(t('editor.toast.removed'))
    return outcome
  }, [afterDecision, campaign, refused, remove, spawnSuccessToast, subjectsOf, t, target, trackAction])

  /**
   * Regenerate, Shorter, More hashtags (ISSUE-3619): the AI rewrites the text the backend holds, so what changed in the
   * form is saved first; then the post's text is PENDING until the campaign query, polling, reads it READY or FAILED.
   * The editor stays on the post either way; `stay` says the save or the request failed.
   */
  const requestText = useCallback(async (current: CampaignPostDTO, draft: PostDraft, action: PostTextAction): Promise<PostEditorOutcome> => {
    if (!campaign || !(await save(current, draft))) return { kind: 'stay' }
    setError(null)
    const tell = (properties: TextActionProperties) =>
      trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_TEXT_ACTION_SUBMITTED, target, campaign, properties)
    const tracked = { textAction: action, ...trackedPostOf(current) }
    const since = Date.now()
    let answer: SocialCampaignDTO
    try {
      answer = await rewrite.mutateAsync({ ...target, postId: current.id, action })
    } catch (failure) {
      if (!isPostTextRefused(failure)) {
        tell({ ...tracked, ...failurePropertiesOf(failure) })
        return refused(failure, subjectsOf(campaign.posts))
      }
      tell({ ...tracked, outcome: 'refused', httpStatus: httpStatusOf(failure) })
      setError({ kind: 'text_refused' })
      return { kind: 'stay' }
    }
    tell({ ...tracked, outcome: 'ok' })
    const asked = answer.posts.find((item) => item.id === current.id)
    // an action the backend could not send is FAILED in its own answer: `textActions` never lists it
    if (asked && postTextStatusOf(asked) !== PostTextStatus.PENDING) tellTextAnswer(asked, action, since, answer)
    return { kind: 'asked' }
  }, [campaign, refused, rewrite, save, subjectsOf, target, tellTextAnswer])

  /**
   * Undo after an AI text action: the backend keeps no earlier version, so the text the client had before the action is
   * saved back as a typed one would be, with the rest of the form. `stay` says the save failed (the error shows).
   */
  const restoreText = useCallback(async (current: CampaignPostDTO, draft: PostDraft, text: string): Promise<PostEditorOutcome> => {
    if (!(await save(current, { ...draft, text }, { action: 'undo_text', textAction: current.textAction ?? undefined }))) return { kind: 'stay' }
    return { kind: 'restored' }
  }, [save])

  /** "Schedule the post": the post written by hand is scheduled at once; the editor closes on it. */
  const createPost = useCallback(async (draft: PostDraft): Promise<PostEditorOutcome> => {
    if (!campaign) return { kind: 'stay' }
    const before = new Set(campaign.posts.map((item) => item.id))
    setError(null)
    const tracked = { action: 'create' as const, mediaCount: draft.media.length, networkCount: draft.networks.length }
    let answer: SocialCampaignDTO
    try {
      answer = await add.mutateAsync({
        ...target,
        body: { date: draft.day, time: draft.time, text: draft.text, media: draft.media.map((media) => media.key), networks: draft.networks },
      })
    } catch (failure) {
      trackAction({ ...tracked, ...failedAction(failure) })
      return refused(failure, new Map())
    }
    const created = answer.posts.find((item) => !before.has(item.id))
    trackAction({ ...tracked, outcome: 'ok', ...trackedPostOf(created) })
    spawnSuccessToast(t('editor.toast.created', { day: formatCampaignDay(draft.day, i18n.language), time: draft.time }))
    closeTo(created ? { postId: created.id } : 'title')
    return { kind: 'closed' }
  }, [add, campaign, closeTo, i18n.language, refused, spawnSuccessToast, t, target, trackAction])

  /** Leaves the post: what changed is saved first ("Save changes", "Done", the close button, Escape). */
  const closeEditor = useCallback(async (current: CampaignPostDTO | null, draft: PostDraft | null): Promise<PostEditorOutcome> => {
    if (current && draft && campaign) {
      const changed = !isEmptyChange(changesOf(draft, current, campaign.timezone, campaign.networks))
      if (!(await save(current, draft))) return { kind: 'stay' }
      if (changed && current.status !== PostStatus.DRAFT) spawnSuccessToast(t('editor.toast.saved'))
    }
    closeTo(current ? { postId: current.id } : 'title')
    return { kind: 'closed' }
  }, [campaign, closeTo, save, spawnSuccessToast, t])

  /** Walks to another post: what changed is saved first. */
  const walk = useCallback(async (current: CampaignPostDTO, draft: PostDraft, to: string): Promise<PostEditorOutcome> => {
    if (!(await save(current, draft))) return { kind: 'stay' }
    return { kind: 'walk', to }
  }, [save])

  /**
   * A change of a post the editor no longer shows did not go through: the page says it, once the unmount settled; when
   * the page went too (another tab of the property), only a warning is left (ids only).
   */
  const campaignId = campaign?.id
  const leftUnsaved = useCallback((postId: string, action: 'saveOnLeave' | 'heldBackOnLeave' | 'actionOnLeave', status?: number) => {
    setError(null)
    window.setTimeout(() => {
      if (mounted.current) {
        showFlash({ kind: 'error' })
        return
      }
      logger.warn('Social campaign post change made in the editor was not saved', undefined, {
        context: { component: 'usePostEditor', action, metadata: { campaignId, postId, status } },
      })
    }, 0)
  }, [campaignId, showFlash])

  /**
   * The editor went without its own buttons (Back, another tab of the property): what changed is sent once it is gone.
   * Only the page can say a failure then (leftUnsaved); a change the editor held back (`savable` false) is not sent.
   */
  const { mutateAsync: sendChange } = update
  const saveOnLeave = useCallback((current: CampaignPostDTO, draft: PostDraft, savable: boolean) => {
    if (!campaign) return
    const changes = changesOf(draft, current, campaign.timezone, campaign.networks)
    if (isEmptyChange(changes)) return
    if (!savable) return leftUnsaved(current.id, 'heldBackOnLeave')
    const tracked = { action: 'edit' as const, via: 'leave' as const, changedFields: joinValues(Object.keys(changes)), ...trackedPostOf(current) }
    // the request's own answer, not the observer's: the observer goes with the page
    sendChange({ ...target, postId: current.id, body: changes }).then(
      () => trackAction({ ...tracked, outcome: 'ok' }),
      (failure: unknown) => {
        trackAction({ ...tracked, ...failedAction(failure) })
        if (mounted.current) refetchCampaign()
        leftUnsaved(current.id, 'saveOnLeave', axios.isAxiosError(failure) ? failure.response?.status : undefined)
      }
    )
  }, [campaign, leftUnsaved, refetchCampaign, sendChange, target, trackAction])

  const { open: openAddress } = address
  const open = useCallback((next: PostAddress, source: SocialCampaignEditorSource = 'other') => {
    if (!isOpen) openedFrom.current = source
    openAddress(next)
  }, [isOpen, openAddress])

  /** Leaves without saving (after a refused save, "Discard changes" then close; a new post, once confirmed) */
  const discardAndClose = useCallback((current: CampaignPostDTO | null) => {
    setError(null)
    if (backedOut) {
      // Back already took its address
      focusAfterClose.current = 'title'
      setBackedOut(null)
      return
    }
    closeTo(current ? { postId: current.id } : 'title')
  }, [backedOut, closeTo])

  /** "Keep editing" once Back took a new post's address: the address comes back, a new entry as when it opened */
  const keepNew = useCallback(() => {
    if (backedOut) openAddress(backedOut)
  }, [backedOut, openAddress])

  /** The editor says after each commit whether the new post it shows holds something */
  const reportNew = useCallback((holdsSomething: boolean) => {
    newHoldsSomething.current = holdsSomething
  }, [])

  return {
    isOpen,
    /** The post shown, null when one is written by hand */
    post: isOpen ? post : null,
    /** The day and time the address gives a post written by hand */
    slot: newPost ? { day: newPost.date, time: newPost.time } : { day: '', time: '' },
    /** Back took the address of the new post shown, which holds something: the editor asks before dropping it */
    leftNew: backedOut !== null,
    error,
    clearError: useCallback(() => setError(null), []),
    /** An action is on its way: the page's own actions wait (their writes would race the editor's) */
    pending: update.isPending || approve.isPending || undo.isPending || remove.isPending || add.isPending || approveAll.isPending || rewrite.isPending,
    /**
     * The AI actions asked here on the campaign's texts, any post, not answered yet (ISSUE-3619): one that leaves the
     * list with `pendingSeen` was answered (`useAskedSocialCampaignTexts`)
     */
    textActions,
    /** Opens the editor; `source` says what opened it, for the analytics */
    open,
    goTo: address.goTo,
    walk,
    saveOnLeave,
    leftUnsaved,
    approvePost,
    launch,
    undoApproval,
    removePost,
    requestText,
    restoreText,
    createPost,
    closeEditor,
    discardAndClose,
    keepNew,
    reportNew,
  }
}
